package er.prateek.crossly.data.model.riddle

import er.prateek.crossly.data.model.DifficultyTier

/**
 * The kinds of rapid-fire math question. [ALGEBRA], [WORD_PROBLEM], [THINK_OF_NUMBER], [GRID_RULE]
 * and [EMOJI_ALGEBRA] are the "logic" kinds, toggled together by `RiddleGenerator.Options.allowLogic`.
 */
enum class RiddleKind { ARITHMETIC, SHAPE_ALGEBRA, SEQUENCE, ALGEBRA, WORD_PROBLEM, THINK_OF_NUMBER, GRID_RULE, EMOJI_ALGEBRA }

/**
 * Symbols drawn inline in a prompt. They are drawn icons, never text: on web, emoji and shapes like
 * "◆" come from a fallback font the app may start without (see tools/webfonts), and a question
 * whose symbols render as empty boxes can't be answered.
 *
 * [SQUARE], [CIRCLE] and [TRIANGLE] are the [RiddleKind.SHAPE_ALGEBRA] variables; [STAR] to
 * [TROPHY] stand in for the fruit in [RiddleKind.EMOJI_ALGEBRA]; [DIAMOND] is the mystery operator
 * in [RiddleKind.GRID_RULE].
 */
enum class ShapeToken { SQUARE, CIRCLE, TRIANGLE, STAR, HEART, FLAME, BELL, BULB, TROPHY, DIAMOND }

/** One piece of a prompt line: either literal text or a drawn symbol. */
sealed interface RiddleSegment {
    data class Text(val value: String) : RiddleSegment
    data class Shape(val token: ShapeToken) : RiddleSegment
}

/**
 * A single question. [prompt] is a list of lines (each a list of segments) so shape-algebra can
 * show several equations; arithmetic and sequences use a single line. [answer] is always a
 * non-negative integer, entered on the number pad. Riddles are generated per attempt and never
 * persisted mid-run, so these are plain (non-serializable) types.
 */
data class Riddle(
    val id: String,
    val kind: RiddleKind,
    val prompt: List<List<RiddleSegment>>,
    val answer: Int,
)

/** One level's worth of questions. */
data class RiddleSet(
    val id: String,
    val tier: DifficultyTier,
    val riddles: List<Riddle>,
)
